import Bugsnag from '@bugsnag/js';
import { useQuery } from '@vue/apollo-composable';
import { computed, Ref, ref, watch, watchEffect } from 'vue';

import { MoneyV2 } from '@/__generated__/storefront/graphql.ts';
import {
  PatternProduct,
  PatternProductsQueryResult,
  PatternProductsQueryVariables,
  ProductExtended,
  Variant
} from '@/shared/composables/patternProducts/patternProducts.types';
import { productTypes } from '@/shared/constants/shopify.js';
import { useConfigStore } from '@/shared/pinia/modules/config';
import { usePatternProductsStore } from '@/shared/pinia/modules/pattern-products';
import {
  getBulkDiscountedUnitPrice,
  getBulkDiscounts,
  getHighestBulkDiscount,
  useBulkDiscounts
} from '@/shared/utils/discounts.ts';
import { contextualError } from '@/shared/utils/error.ts';
import {
  shopifyGidToId,
  shopifyIdToGid
} from '@/shared/utils/shopify/id-formatter.ts';

import {
  patternProductQuery,
  patternSizesQuery
} from './patternProductsStorefront.queries.ts';

export const usePatternProducts = (
  patternId: string,
  initialPatternProductsList: PatternProduct[],
  showOnlyYarn: Ref<boolean>
) => {
  const configStore = useConfigStore();
  const store = usePatternProductsStore();

  // Initialize store if not already done
  if (store.allPatternProductsForSize.length === 0) {
    store.initialize(initialPatternProductsList);
  }

  const allHydratedProducts = ref<ProductExtended[]>([]);
  const queriedProductIds = ref<Set<string>>(new Set());

  const { result: patternSizesResult } = useQuery(
    patternSizesQuery,
    { patternId: shopifyIdToGid(patternId, 'Product') },
    { clientId: 'storefront' }
  );

  const patternSizes = computed(
    () => patternSizesResult?.value?.product?.variants?.nodes || []
  );

  const productIds = computed(() => {
    return store.activePatternProducts.map((pp) => pp.product_id);
  });

  const newProductIds = computed(() => {
    return productIds.value.filter((id) => !queriedProductIds.value.has(id));
  });

  const incompletePatternProducts = computed(() => {
    if (loading.value) return false;

    // Get active pattern products from store
    const activePatternProducts = store.activePatternProducts;

    if (activePatternProducts.length <= 0) return false;

    // Count pattern products that are truly incomplete (not just filtered out)
    // This includes:
    // 1. Products that couldn't be fetched from API
    // 2. Pattern products where the specific variant doesn't exist
    const incompleteCount = activePatternProducts.reduce((count, pp) => {
      const hydratedProduct = hydratedProductList.value.find(
        (p: ProductExtended) => p.id === pp.product_id
      );

      // If we don't have the product data, it's incomplete
      if (!hydratedProduct) return count + 1;

      // If we don't have the specific variant, it's incomplete
      const selectedVariant = hydratedProduct.variants.nodes.find(
        (v: Variant) => v.id === pp.variant_id
      );

      return !selectedVariant ? count + 1 : count;
    }, 0);

    return incompleteCount;
  });

  const {
    result: resultPatternProducts,
    loading,
    error: patternProductsError
  } = useQuery<PatternProductsQueryResult, PatternProductsQueryVariables>(
    patternProductQuery,
    () => ({
      ids: Array.from(new Set(newProductIds.value)),
      country: window?.Shopify?.country
    }),
    {
      clientId: 'storefront'
    }
  );

  const patternProductsLoading = computed(() => {
    return loading.value && allHydratedProducts.value.length === 0;
  });

  // Watch for new query results and merge them
  watchEffect(() => {
    const queryResult =
      resultPatternProducts.value?.nodes?.filter((a) => a) ?? [];
    if (queryResult.length > 0) {
      const processedResults = queryResult.map((product) => {
        const parsedMetafields = product.metafields.reduce<
          Record<string, string>
        >((metaFieldsObject, metafield) => {
          if (metafield) {
            metaFieldsObject[metafield.key] = metafield.value;
          }
          return metaFieldsObject;
        }, {});

        // calculate bulk discounts from metafield
        const bulkDiscountsMetafield = parsedMetafields['bulk_discounts'];
        const minPrice = product.priceRange.minVariantPrice;
        const maxPrice = product.compareAtPriceRange.minVariantPrice;
        const isOnSale =
          parseFloat(maxPrice.amount) > parseFloat(minPrice.amount);

        const displayBulkDiscount = useBulkDiscounts(
          isOnSale,
          !!bulkDiscountsMetafield
        );
        const bulkDiscounts = displayBulkDiscount
          ? getBulkDiscounts(
              bulkDiscountsMetafield,
              parseFloat(minPrice.amount)
            )
          : [];
        const highestBulkDiscount = getHighestBulkDiscount(bulkDiscounts);

        return {
          ...product,
          brand: parsedMetafields['brand'] || parsedMetafields['pattern_kind'],
          bulkDiscountPercentage: highestBulkDiscount?.discount_percentage || 0,
          bulkDiscounts
        };
      });

      // Merge new results with existing ones, avoiding duplicates
      const existingIds = new Set(allHydratedProducts.value.map((p) => p.id));
      const newResults: ProductExtended[] = processedResults.filter(
        (p) => !existingIds.has(p.id)
      );
      allHydratedProducts.value = [...allHydratedProducts.value, ...newResults];

      // Mark these product IDs as queried
      newProductIds.value.forEach((id) => {
        queriedProductIds.value.add(id);
      });
    }
  });

  const hydratedProductList = computed<ProductExtended[]>(() => {
    // Return only the products that are currently in the pattern
    const currentProductIds = new Set(productIds.value);
    return allHydratedProducts.value.filter((product) =>
      currentProductIds.has(product.id)
    );
  });

  watchEffect(() => {
    if (!Bugsnag.isStarted()) {
      return;
    }

    if (incompletePatternProducts.value) {
      const incompletePatternProductsError = contextualError(
        'usePatternProducts',
        'Incomplete pattern products, some products are null',
        {
          data: initialPatternProductsList
        }
      );

      if (incompletePatternProductsError) {
        Bugsnag.notify(incompletePatternProductsError);
      }
    }

    if (patternProductsError.value) {
      const patternProductsContextualError = contextualError(
        'usePatternProducts',
        patternProductsError.value,
        {
          data: initialPatternProductsList
        }
      );

      if (patternProductsContextualError) {
        Bugsnag.notify(patternProductsContextualError);
      }
    }
  });

  const getVariantWeight = (variant: Variant, productType: string) => {
    const metafield = variant.metafield?.value;
    if (metafield && productType === productTypes.YARN) {
      const dimensions = JSON.parse(metafield);
      return `${dimensions.weight}g`;
    }

    return '';
  };

  const getDiscountPercentage = (
    price: MoneyV2,
    compareAtPrice: MoneyV2 | null
  ) => {
    const oldPrice = compareAtPrice ? parseFloat(compareAtPrice.amount) : null;

    if (oldPrice && oldPrice > 0) {
      return Math.floor(
        ((oldPrice - parseFloat(price.amount)) * 100) / oldPrice
      );
    }

    return 0;
  };

  const handleSizeChange = (size: string) => {
    const selectedPatternSize = patternSizes.value?.find(
      (ps) => ps.title === size
    );

    const ppMetafield = selectedPatternSize?.metafield?.value;
    if (!ppMetafield) return;

    const newSizeData: PatternProduct[] = JSON.parse(ppMetafield);
    store.handleSizeChange(size, newSizeData);
  };

  /**
   * Combined product metadata computation for efficiency.
   * Pre-computes both availability and type information in a single pass.
   */
  const productMetadata = computed<
    Map<number, { isAvailable: boolean; productType: string }>
  >(() => {
    const metadataMap = new Map<
      number,
      { isAvailable: boolean; productType: string }
    >();

    store.allPatternProductsForSize.forEach(
      (pp: PatternProduct, sourceIndex: number) => {
        // Skip products with quantity 0 or invalid
        if (!store.hasRequiredData(pp)) {
          metadataMap.set(sourceIndex, {
            isAvailable: false,
            productType: ''
          });
          return;
        }

        const hydratedProduct = hydratedProductList.value.find(
          (p: ProductExtended) => p.id === pp.product_id
        );

        if (!hydratedProduct) {
          metadataMap.set(sourceIndex, {
            isAvailable: false,
            productType: ''
          });
          return;
        }

        const selectedVariant = hydratedProduct.variants.nodes.find(
          (v: Variant) => v.id === pp.variant_id
        );

        const isAvailable = !!selectedVariant;
        metadataMap.set(sourceIndex, {
          isAvailable,
          productType: hydratedProduct.productType
        });
      }
    );

    return metadataMap;
  });

  /**
   * Helper function to determine if a product passes current filters
   */
  const passesCurrentFilters = (sourceIndex: number) => {
    const metadata = productMetadata.value.get(sourceIndex);
    if (!metadata?.isAvailable) return false;

    const matchesFilter = showOnlyYarn.value
      ? metadata.productType === productTypes.YARN
      : true;
    return matchesFilter;
  };

  /**
   * Maps visible product indices to their corresponding allPatternProductsForSize indices.
   * Uses pre-computed metadata to avoid expensive re-computations.
   */
  const visibleToSourceIndexMap = computed<number[]>(() => {
    const visibleIndices: number[] = [];

    productMetadata.value.forEach((_, sourceIndex) => {
      if (passesCurrentFilters(sourceIndex)) {
        visibleIndices.push(sourceIndex);
      }
    });

    return visibleIndices;
  });

  // Memoization cache for enriched products to avoid expensive recalculations
  // Using plain Map (not reactive) since invalidation is handled explicitly via watch
  const enrichedProductCache = new Map<string, PatternProduct>();

  /**
   * Computed property that aggregates total quantities per product_id
   * across currently visible pattern products. This ensures bulk discounts
   * are calculated based only on what the user is currently viewing and considering for purchase.
   */
  const productQuantityAggregates = computed<Map<string, number>>(() => {
    return store.allPatternProductsForSize.reduce(
      (
        aggregates: Map<string, number>,
        pp: PatternProduct,
        sourceIndex: number
      ) => {
        if (store.hasRequiredData(pp) && passesCurrentFilters(sourceIndex)) {
          const currentTotal = aggregates.get(pp.product_id) || 0;
          aggregates.set(pp.product_id, currentTotal + pp.quantity);
        }
        return aggregates;
      },
      new Map<string, number>()
    );
  });

  // Helper function to create cache key for a pattern product
  // Includes sourceIndex to handle duplicate products correctly and total quantity for bulk discount calculation
  const getCacheKey = (pp: PatternProduct, sourceIndex: number) => {
    const totalQuantity =
      productQuantityAggregates.value.get(pp.product_id) || pp.quantity;
    return `${pp.product_id}-${pp.variant_id}-${pp.quantity}-${totalQuantity}-${sourceIndex}`;
  };

  // Helper function to enrich a single pattern product
  const enrichPatternProduct = (
    pp: PatternProduct,
    sourceIndex: number
  ): PatternProduct => {
    const cacheKey = getCacheKey(pp, sourceIndex);

    // Return cached version if available
    if (enrichedProductCache.has(cacheKey)) {
      return enrichedProductCache.get(cacheKey)!;
    }

    const hydratedProduct = hydratedProductList.value.find(
      (p: ProductExtended) => p.id === pp.product_id
    );

    if (!hydratedProduct) {
      enrichedProductCache.set(cacheKey, pp);
      return pp;
    }

    const selectedVariant = hydratedProduct.variants.nodes.find(
      (v: Variant) => v.id === pp.variant_id
    );

    if (!selectedVariant) {
      enrichedProductCache.set(cacheKey, pp);
      return pp;
    }

    const variantId = shopifyGidToId(selectedVariant?.id);
    const productId = shopifyGidToId(hydratedProduct?.id);

    let compareAtPrice: MoneyV2 | null = selectedVariant.compareAtPrice;
    let price = selectedVariant.price;

    const isOnSale = !!(
      compareAtPrice && Number(compareAtPrice.amount) > Number(price.amount)
    );
    if (useBulkDiscounts(isOnSale, hydratedProduct.bulkDiscounts?.length > 0)) {
      // Use aggregated quantity across all lines with the same product_id for bulk discount calculation
      const totalQuantityForProduct =
        productQuantityAggregates.value.get(pp.product_id) || pp.quantity;

      const bulkDiscountPrice = getBulkDiscountedUnitPrice(
        hydratedProduct.bulkDiscounts,
        totalQuantityForProduct,
        Number(selectedVariant.price.amount)
      );

      if (bulkDiscountPrice) {
        price = {
          amount: bulkDiscountPrice,
          currencyCode: window.Shopify.currency.active
        };

        //the original variant price is the "old" price when a bulk discount is applied
        compareAtPrice = selectedVariant.price;

        const noChangeDetected =
          bulkDiscountPrice === Number(compareAtPrice?.amount);

        if (noChangeDetected) compareAtPrice = null;
      }
    }

    const enrichedProduct = {
      ...pp,
      product: {
        id: hydratedProduct.id,
        idNumeric: productId,
        title: hydratedProduct.title,
        handle: hydratedProduct.handle,
        brand: hydratedProduct.brand,
        featuredImage: hydratedProduct.featuredImage,
        productType: hydratedProduct.productType,
        price,
        compareAtPrice,
        points: configStore.calculatedPoints(price.amount * 100),
        saleDiscountPercentage: getDiscountPercentage(
          selectedVariant.price,
          selectedVariant.compareAtPrice
        ),
        bulkDiscountPercentage: hydratedProduct.bulkDiscountPercentage,
        bulkDiscounts: hydratedProduct.bulkDiscounts,
        variant: {
          ...selectedVariant,
          weight: getVariantWeight(
            selectedVariant,
            hydratedProduct.productType
          ),
          idNumeric: variantId
        },
        variants: hydratedProduct.variants
      }
    };

    // Cache the result
    enrichedProductCache.set(cacheKey, enrichedProduct);
    return enrichedProduct;
  };

  /**
   * UI-ready pattern products with full Shopify data and filtering applied.
   * Uses memoization to avoid expensive recalculations.
   */
  const visiblePatternProducts = computed<PatternProduct[]>(() => {
    return visibleToSourceIndexMap.value.map((sourceIndex) => {
      const pp = store.allPatternProductsForSize[sourceIndex];
      return enrichPatternProduct(pp, sourceIndex);
    });
  });

  // Clear cache when hydrated products, product quantities, or pattern products change to ensure fresh data
  watch(
    [
      hydratedProductList,
      productQuantityAggregates,
      () => store.allPatternProductsForSize
    ],
    () => {
      enrichedProductCache.clear();
    },
    { deep: true }
  );

  const updateQuantityByDisplayIndex = (
    displayIndex: number,
    quantity: number
  ) => {
    // Use the direct index mapping to find the correct source product
    const sourceIndex = visibleToSourceIndexMap.value[displayIndex];
    if (sourceIndex !== undefined) {
      store.updateQuantityBySourceIndex(sourceIndex, quantity);
    }
  };

  const updateVariantByDisplayIndex = (
    displayIndex: number,
    productId: string,
    variantId: string
  ) => {
    // Use the direct index mapping to find the correct source product
    const sourceIndex = visibleToSourceIndexMap.value[displayIndex];
    if (sourceIndex !== undefined) {
      store.updateVariantBySourceIndex(sourceIndex, productId, variantId);
    }
  };

  return {
    hydratedProductList,
    patternProductsLoading,
    incompletePatternProducts,
    patternSizes,
    getVariantWeight,
    getDiscountPercentage,
    handleSizeChange,
    visiblePatternProducts,
    updateQuantityByDisplayIndex,
    updateVariantByDisplayIndex
  };
};
